import type { OrderSignedInboxItem } from '@seen/core/models/inbox'
import {
  OrderCategory,
  orderCategoryLabel,
} from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { cn } from '@seen/ui'
import { IconWriting } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowAvatar } from '#src/pages/inbox/row/InboxRowAvatar.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowIcon } from '#src/pages/inbox/row/InboxRowIcon.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'

interface OrderSignedInboxItemRowProps {
  item: OrderSignedInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

// @deprecated see https://seenhealth.slack.com/archives/C082KPPHDLN/p1759416043926339?thread_ts=1759358095.438529&cid=C082KPPHDLN
// retained for backwards compatibility
export function OrderSignedInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: OrderSignedInboxItemRowProps): ReactNode {
  const { data: signer, isLoading: isLoadingSigner } = useRead(
    ExtendedPractitioner,
    item.signedBy
  )
  const { data: order } = useRead(Order, item.order)
  const { data: participant } = useRead(Participant, order?.participant)

  const title = getOrderTitle(order)
  const categoryLabel =
    order?.category && order.category !== OrderCategory.Misc
      ? orderCategoryLabel(order.category)
      : ''

  return (
    <InboxRow
      to={item.actionUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
    >
      <InboxRowAvatar
        profile={signer?.reference}
        icon={
          <InboxRowIcon>
            <IconWriting className="h-3.5 w-3.5 text-white" />
          </InboxRowIcon>
        }
      />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          isLoading={isLoadingSigner}
          badges={
            participant && (
              <ParticipantTaskRowBadge participant={participant.reference} />
            )
          }
        >
          <span className={cn('relative top-0.5 inline-flex')}>
            <IconOrderStatus
              status={order?.status}
              isSubmitting={order?.isSubmitting}
              className="size-4"
            />
          </span>{' '}
          <span className="text-foreground-secondary">
            {order?.seenIdentifier}
          </span>{' '}
          {!!categoryLabel && <span>{categoryLabel}</span>}
          {!!categoryLabel && !!title && <span>: </span>}
          {!!title && <span>{title}</span>}
        </InboxRowText>
        <InboxRowSubtext isLoading={isLoadingSigner}>
          {signer?.name}
          {!!signer?.roles?.length && <> {signer.roles.join(', ')}</>}
          {(signer || isLoadingSigner) && <> signed the order</>}
        </InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}
